import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { IconButton } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { MAX_COMPETITORS } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/LLMSetupConst";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

import type { IRawCompetitor } from "../../types";

import { CompetitorField } from "./CompetitorField";
import { getCompetitorDomainErrorKeys } from "./utils";

interface ICompetitorRowError {
    domain?: string;
    brand?: string;
}

interface ICompetitorsSectionProps {
    title: string;
    competitors: IRawCompetitor[];
    trackedDomains: IWebsiteItem[];
    onCompetitorChange: (index: number, competitor: IRawCompetitor) => void;
    onCompetitorDelete: (index: number) => void;
    errorsByIndex?: Record<number, ICompetitorRowError>;
    hasGenerationError?: boolean;
    isReadOnly?: boolean;
    isLoading?: boolean;
}

const isCompetitorComplete = (competitor: IRawCompetitor): boolean =>
    !!competitor.domain && !!competitor.brands?.length;

export const CompetitorsSection = ({
    title,
    competitors,
    trackedDomains,
    isLoading,
    onCompetitorChange,
    onCompetitorDelete,
    hasGenerationError,
    errorsByIndex,
    isReadOnly,
}: ICompetitorsSectionProps) => {
    const completeCount = competitors?.filter(isCompetitorComplete).length ?? 0;
    const lastCompetitor = competitors?.[competitors.length - 1];
    const isLastRowComplete = !lastCompetitor || isCompetitorComplete(lastCompetitor);
    const canAddCompetitor = competitors?.length < MAX_COMPETITORS && isLastRowComplete;

    const createNewCompetitor = (): void => {
        const index = competitors.length;

        if (index < MAX_COMPETITORS) {
            onCompetitorChange(index, { domain: null, brands: [] });
        }
    };

    const domainErrorsByIndex = useMemo<Map<number, string>>(
        () =>
            new Map(
                [...getCompetitorDomainErrorKeys(competitors, trackedDomains)].map(([index, errorKey]) => [
                    index,
                    translate(errorKey),
                ]),
            ),
        [competitors, trackedDomains],
    );

    return (
        <Flex
            data-automation="llm-competitors-section"
            flexDirection="column"
            width="100%"
            gap="12px"
        >
            <Flex
                justifyContent="space-between"
                alignItems="center"
            >
                <Text
                    fontVariant="subtitle2"
                    color={colorsPalettes.carbon[400]}
                    data-automation="llm-competitors-section-title"
                >
                    {title}
                </Text>
                <Text
                    data-automation="llm-competitors-section-counter"
                    fontVariant="caption"
                    color={colorsPalettes.carbon[300]}
                >
                    {translate("llm.competitors_section.counter", {
                        count: completeCount,
                        maxCount: MAX_COMPETITORS,
                    })}
                </Text>
            </Flex>
            {hasGenerationError ? (
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.carbon[500]}
                >
                    {translate("llm.competitors_section.generation-error")}
                </Text>
            ) : null}
            <Flex
                flexDirection="column"
                gap="12px"
                width="100%"
                minWidth="0"
            >
                {competitors?.map((competitor, index) => (
                    <CompetitorField
                        key={`${competitor?.domain?.id ?? "empty"}-${index}`}
                        competitor={competitor}
                        onChange={(c) => onCompetitorChange(index, c)}
                        onRemove={() => onCompetitorDelete(index)}
                        domainError={domainErrorsByIndex.get(index) ?? errorsByIndex?.[index]?.domain}
                        brandError={errorsByIndex?.[index]?.brand}
                        autoFocus={!competitor?.domain && index === competitors.length - 1}
                        isRemovable={!isReadOnly}
                        isLoading={!!isLoading}
                        isReadOnly={isReadOnly}
                    />
                ))}
            </Flex>
            {!isReadOnly && !isLoading && competitors?.length < MAX_COMPETITORS ? (
                <Box>
                    <IconButton
                        type="flat"
                        iconName="add"
                        onClick={createNewCompetitor}
                        isDisabled={!canAddCompetitor}
                        data-automation="llm-competitors-section-add"
                    >
                        {translate("llm.competitors_section.add")}
                    </IconButton>
                </Box>
            ) : null}
        </Flex>
    );
};
